import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer, Input, notification } from 'antd';
import isEqual from 'lodash/isEqual';
import OmniPicker from 'components/OmniPicker';
import ChecklistEditorActions from './ChecklistEditorActions';
import ChecklistSectionList from './ChecklistSectionList';
import confirmDanger from './confirmDanger';
import { draftProblem, findItem, itemIDs, newDraft, patchById, toDraft } from './checklistDraft';
import './ChecklistEditor.scss';

// Matches the version-conflict error from eca-backend UpdateChecklist in db/checklist.go.
const STALE_HINT = 'changed by someone else';

const ChecklistEditorDrawer = props => {
	const { open, checklistID, onClose, onSaved, onDeleted, event, checklists, groups } = props;
	const {
		fetchGroups,
		fetchChecklistCounts,
		saveChecklist,
		deleteChecklist,
		reloadChecklist,
	} = props;
	// Falls back to the id so a checklist missing from the store can never be saved as a new one.
	const [draft, setDraft] = useState(() =>
		checklistID
			? toDraft(checklists.find(checklist => checklist.id === checklistID) ?? { id: checklistID })
			: newDraft()
	);
	const [baseline, setBaseline] = useState(draft);
	const [counts, setCounts] = useState(checklistID ? null : {});
	const [stale, setStale] = useState(false);
	const [busy, setBusy] = useState(null);
	const [linkTarget, setLinkTarget] = useState(null);
	const dirty = !isEqual(draft, baseline);
	const problem = draftProblem(draft);
	const savedItemIDs = useMemo(() => itemIDs(baseline), [baseline]);

	const reset = next => {
		setDraft(next);
		setBaseline(next);
		setStale(false);
	};

	useEffect(() => {
		if (!open) return;
		fetchGroups(event.id);
		if (!checklistID) return;
		let active = true;
		fetchChecklistCounts(checklistID).then(next => active && setCounts(next));
		return () => {
			active = false;
		};
	}, [open]);

	const changeSections = useCallback(
		change =>
			setDraft(current => ({
				...current,
				content: { ...current.content, sections: change(current.content.sections) },
			})),
		[]
	);

	const run = async (kind, action) => {
		setBusy(kind);
		try {
			await action();
		} catch (error) {
			notification.error({ message: error.message });
			if (error.message.includes(STALE_HINT)) setStale(true);
		} finally {
			setBusy(null);
		}
	};

	const handleSave = () =>
		run('save', async () => {
			const sent = draft;
			const saved = await saveChecklist(sent);
			const ids = { id: saved.id, version: saved.version };
			setDraft(current => ({ ...current, ...ids }));
			setBaseline({ ...sent, ...ids });
			setStale(false);
			notification.success({ message: 'Checklist saved' });
			if (onSaved) onSaved(saved);
		});

	const reload = () =>
		run('reload', async () => {
			const latest = await reloadChecklist(draft.id);
			if (latest) {
				reset(toDraft(latest));
				setCounts(null);
				setCounts(await fetchChecklistCounts(draft.id));
				return;
			}
			notification.warning({ message: 'This checklist was deleted' });
			if (onDeleted) onDeleted(draft.id);
			onClose();
		});

	const handleReload = () => {
		if (!dirty) return reload();
		confirmDanger('Reload and discard your changes?', 'Discard and reload', reload);
	};

	const handleDelete = () =>
		run('delete', async () => {
			await deleteChecklist(draft.id);
			notification.success({ message: 'Checklist deleted' });
			if (onDeleted) onDeleted(draft.id);
			onClose();
		});

	const handleClose = () => {
		if (!dirty) return onClose();
		confirmDanger('Discard unsaved changes?', 'Discard', onClose);
	};

	const linkItem = linkTarget && findItem(draft.content.sections, linkTarget);
	const handlePickLink = onPress => {
		const { sectionID, itemID } = linkTarget;
		changeSections(sections =>
			patchById(sections, sectionID, section => ({
				items: patchById(section.items, itemID, { on_press: onPress }),
			}))
		);
		setLinkTarget(null);
	};

	return (
		<Drawer
			open={open}
			width="100%"
			destroyOnClose
			onClose={handleClose}
			className="ChecklistEditor"
			title={
				<div className="ChecklistEditor-title">
					<Input
						value={draft.name}
						placeholder="Checklist name"
						aria-label="Checklist name"
						autoFocus={!draft.id}
						onChange={e => setDraft(current => ({ ...current, name: e.target.value }))}
					/>
					{dirty && <span className="ChecklistEditor-dirty">Unsaved changes</span>}
				</div>
			}
			extra={
				<ChecklistEditorActions
					busy={busy}
					stale={stale}
					canDelete={!!draft.id}
					problem={dirty ? problem : null}
					canSave={dirty && !problem}
					onReload={handleReload}
					onDelete={handleDelete}
					onSave={handleSave}
				/>
			}
		>
			<ChecklistSectionList
				sections={draft.content.sections}
				event={event}
				groups={groups}
				counts={counts}
				savedItemIDs={savedItemIDs}
				onChange={changeSections}
				onPickLink={setLinkTarget}
			/>
			<OmniPicker
				event={event}
				visible={!!linkTarget}
				onCancel={() => setLinkTarget(null)}
				handlePickItem={handlePickLink}
				onPress={linkItem?.on_press || undefined}
				linkPicker={event.single_page_app !== 'TRUE'}
				from="checklist"
			/>
		</Drawer>
	);
};

export default ChecklistEditorDrawer;
